import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Copy, Check, KeyRound, Plus, Trash2, Search, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { AccountLayout } from '@/components/account/AccountLayout'
import { AccountPageHeader } from '@/components/account/AccountPageHeader'
import { UsageOverview } from '@/components/account/UsageOverview'
import { useAuth } from '@/lib/auth'
import { useActiveOrg, useUserProfile } from '@/lib/user-profile'
import { useOrgMemberDirectory } from '@/lib/org'
import {
  listMyApiKeys,
  generateMyApiKey,
  revokeMyApiKey,
  type ApiKeyRow,
  type GeneratedKey,
} from '@/lib/api/api-keys'

// ---- expiration dropdown ----------------------------------------------------

interface ExpirationOption { label: string; days: number | null }
const EXPIRATION_OPTIONS: readonly ExpirationOption[] = [
  { label: 'Never expires', days: null },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '365 days', days: 365 },
] as const

function expiresAtForOption(days: number | null): string | null {
  if (days === null) return null
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d.toISOString()
}

// ---- per-key status / dates -------------------------------------------------

function statusOf(k: ApiKeyRow): 'active' | 'expired' | 'revoked' {
  if (k.revoked_at) return 'revoked'
  if (k.expires_at && new Date(k.expires_at) < new Date()) return 'expired'
  return 'active'
}

function shortDate(s: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// ---- page -------------------------------------------------------------------

export function AccountKeysPage() {
  const { user, loading: authLoading } = useAuth()
  const { org, refresh: refreshOrg } = useActiveOrg()
  const { refresh: refreshProfile } = useUserProfile()
  const activeOrgId = org?.organization_id ?? null
  const isVerified = Boolean(user?.email_confirmed_at)
  // Resolve created_by uuids to member names (names-only directory RPC).
  const { names: memberNames } = useOrgMemberDirectory(activeOrgId)

  // Single source of truth for scope: the user's active org (driven by
  // the standard ORGANIZATION switcher in the page header). View, KPI,
  // and "generate in" all key off it. The previous separate
  // viewOrgId / generateOrgId + allowAll filter was inconsistent with
  // the other /account/* pages — switching is now: change active org
  // via header dropdown → everything on the page follows.
  const [keys, setKeys] = useState<ApiKeyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [expirationDays, setExpirationDays] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [justGenerated, setJustGenerated] = useState<GeneratedKey | null>(null)
  const [copied, setCopied] = useState(false)
  // State-driven revoke confirmation modal — replaces the browser confirm()
  // popup which looks dated and breaks the modal-in-page feel of the rest
  // of the app.
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null)
  const [revoking, setRevoking] = useState(false)

  // Existing-keys table controls: search by name/prefix, filter by status,
  // and sort by name / last used / expiry.
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'expired' | 'revoked'>('all')
  const [sortCol, setSortCol] = useState<'name' | 'last_used_at' | 'expires_at'>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  function toggleSort(col: 'name' | 'last_used_at' | 'expires_at') {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortCol(col)
      setSortDir('asc')
    }
  }

  const refresh = useCallback(async () => {
    if (!activeOrgId) {
      setKeys([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const rows = await listMyApiKeys(activeOrgId)
      setKeys(rows)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load keys')
    } finally {
      setLoading(false)
    }
  }, [activeOrgId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
  }, [refresh])

  async function onCreate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!newName.trim()) return
    if (!activeOrgId) {
      setError('No active organization selected.')
      return
    }
    setCreating(true)
    setError(null)
    try {
      const expiresAt = expiresAtForOption(expirationDays)
      const generated = await generateMyApiKey(newName.trim(), expiresAt, activeOrgId)
      setJustGenerated(generated)
      setNewName('')
      setExpirationDays(null)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate key')
    } finally {
      setCreating(false)
    }
  }

  async function confirmRevoke() {
    if (!revokeTarget) return
    setRevoking(true)
    setError(null)
    try {
      await revokeMyApiKey(revokeTarget.id)
      setRevokeTarget(null)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke key')
    } finally {
      setRevoking(false)
    }
  }

  async function copyGenerated() {
    if (!justGenerated) return
    try {
      await navigator.clipboard.writeText(justGenerated.plaintext_key)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard blocked — fall through */
    }
  }

  const activeKeyCount = useMemo(
    () => keys.filter((k) => statusOf(k) === 'active').length,
    [keys],
  )

  const visibleKeys = useMemo(() => {
    const q = search.trim().toLowerCase()
    // null dates sort to the end in either direction (never-used /
    // never-expires shouldn't crowd the top).
    const timeKey = (s: string | null) => (s ? Date.parse(s) : Number.POSITIVE_INFINITY)
    const filtered = keys.filter((k) => {
      if (statusFilter !== 'all' && statusOf(k) !== statusFilter) return false
      if (!q) return true
      return k.name.toLowerCase().includes(q) || k.key_prefix.toLowerCase().includes(q)
    })
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      if (sortCol === 'name') return dir * a.name.localeCompare(b.name)
      const av = timeKey(a[sortCol])
      const bv = timeKey(b[sortCol])
      if (av === bv) return 0
      // Keep nulls last regardless of direction.
      if (av === Number.POSITIVE_INFINITY) return 1
      if (bv === Number.POSITIVE_INFINITY) return -1
      return dir * (av - bv)
    })
  }, [keys, search, statusFilter, sortCol, sortDir])

  if (!authLoading && !user) return <Navigate to="/sign-in" replace />

  return (
    <AccountLayout>
      <AccountPageHeader
        title="API Keys"
        subtitle="Generate, revoke, and set expirations. Plaintext keys are shown once on creation and never again."
        orgId={activeOrgId ?? ''}
        onOrgChange={async () => {
          await Promise.all([refreshProfile(), refreshOrg()])
        }}
      />

      <UsageOverview orgId={activeOrgId} activeKeyCount={activeKeyCount} hideCharts />

      {!isVerified ? (
        <div
          className={[
            'mt-6 rounded-[var(--radius-lg)] border border-[var(--color-signal)]/50',
            'bg-[var(--color-signal-soft)] px-4 py-3',
            'text-[13.5px] text-[var(--color-text)]',
          ].join(' ')}
          role="status"
        >
          Verify your email address before generating keys. We sent a link when
          you signed up — check your inbox (and spam).
        </div>
      ) : null}

      {/* Generate card */}
      <form
        onSubmit={onCreate}
        className={[
          'mt-6 rounded-[var(--radius-lg)] p-5 sm:p-6',
          'border border-[var(--color-border)] bg-[var(--color-elev)]',
          'flex flex-col gap-3 sm:flex-row sm:items-end',
        ].join(' ')}
      >
        <label className="flex-1 flex flex-col gap-1.5">
          <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
            New key name
          </span>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. local-dev, production-worker"
            maxLength={120}
            required
            disabled={!isVerified}
            className={[
              'w-full px-3 py-2.5 rounded-[var(--radius-DEFAULT)]',
              'bg-[var(--color-surface)] border border-[var(--color-border)]',
              'text-[14px] text-[var(--color-text)]',
              'placeholder:text-[var(--color-muted)]/60',
              'focus:outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/30',
              'disabled:opacity-60 disabled:cursor-not-allowed',
              'transition-colors',
            ].join(' ')}
          />
        </label>
        {/* New keys mint into the active org (driven by the standard
            ORGANIZATION switcher in the page header). To generate in a
            different org, switch active org from the top dropdown. */}
        <label className="flex flex-col gap-1.5 sm:w-44">
          <span className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
            Expires
          </span>
          <select
            value={expirationDays === null ? '' : String(expirationDays)}
            onChange={(e) =>
              setExpirationDays(e.target.value === '' ? null : Number(e.target.value))
            }
            disabled={!isVerified}
            className={[
              'w-full px-3 py-2.5 rounded-[var(--radius-DEFAULT)]',
              'bg-[var(--color-surface)] border border-[var(--color-border)]',
              'text-[14px] text-[var(--color-text)] cursor-pointer',
              'focus:outline-none focus:border-[var(--color-accent)]',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            ].join(' ')}
          >
            {EXPIRATION_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.days === null ? '' : String(opt.days)}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={creating || !isVerified || !newName.trim()}
          className={[
            'inline-flex items-center justify-center gap-1.5',
            'px-4 py-2.5 rounded-[var(--radius-DEFAULT)]',
            'bg-[var(--color-accent)] text-[var(--color-surface)]',
            'text-[14px] font-medium',
            'hover:bg-[var(--color-accent-hover)] transition-colors',
            'disabled:opacity-60 disabled:cursor-not-allowed',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-elev)]',
            'cursor-pointer',
          ].join(' ')}
        >
          <Plus size={16} />
          {creating ? 'Generating…' : 'Generate key'}
        </button>
      </form>

      {error ? (
        <p
          role="alert"
          className="mt-4 text-[13px] text-[var(--color-signal)] bg-[var(--color-signal-soft)] px-3 py-2 rounded-[var(--radius-DEFAULT)]"
        >
          {error}
        </p>
      ) : null}

      {/* One-time plaintext modal */}
      {justGenerated ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
        >
          <div
            className={[
              'w-full max-w-lg rounded-[var(--radius-lg)] p-6',
              'border border-[var(--color-accent)]/50',
              'bg-[var(--color-elev)] shadow-[var(--shadow-results)]',
            ].join(' ')}
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-[var(--color-accent-soft)] text-[var(--color-accent)]">
                <KeyRound size={20} />
              </span>
              <div>
                <h2 className="text-[17px] font-semibold tracking-tight text-[var(--color-text-strong)] m-0">
                  Copy this key now
                </h2>
                <p className="mt-1 text-[13px] text-[var(--color-muted)]">
                  This is the only time the plaintext will be shown. The
                  database stores only its SHA-256 hash.
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-2">
              <code
                className={[
                  'flex-1 px-3 py-2.5 rounded-[var(--radius-DEFAULT)]',
                  'bg-[var(--color-surface)] border border-[var(--color-border)]',
                  'font-[var(--font-mono)] text-[12px] text-[var(--color-text)] break-all',
                ].join(' ')}
              >
                {justGenerated.plaintext_key}
              </code>
              <button
                type="button"
                onClick={copyGenerated}
                className={[
                  'inline-flex items-center justify-center w-10 h-10 rounded-[var(--radius-DEFAULT)]',
                  'border border-[var(--color-border)]',
                  'text-[var(--color-text)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/50',
                  'transition-colors cursor-pointer',
                ].join(' ')}
                aria-label="Copy key"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setJustGenerated(null)
                  setCopied(false)
                }}
                className={[
                  'px-4 py-2 rounded-[var(--radius-DEFAULT)]',
                  'border border-[var(--color-border)] text-[14px]',
                  'text-[var(--color-text)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/50',
                  'transition-colors cursor-pointer',
                ].join(' ')}
              >
                I&apos;ve saved it
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Revoke confirmation modal */}
      {revokeTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="revoke-heading"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
        >
          <div
            className={[
              'w-full max-w-md rounded-[var(--radius-lg)] p-6',
              'border border-[var(--color-signal)]/50',
              'bg-[var(--color-elev)] shadow-[var(--shadow-results)]',
            ].join(' ')}
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-[var(--color-signal-soft)] text-[var(--color-signal)]">
                <Trash2 size={18} aria-hidden />
              </span>
              <div className="min-w-0">
                <h2 id="revoke-heading" className="text-[17px] font-semibold tracking-tight text-[var(--color-text-strong)] m-0">
                  Revoke {revokeTarget.name}?
                </h2>
                <p className="mt-1 text-[13px] text-[var(--color-muted)]">
                  Any client using <span className="font-[var(--font-mono)] text-[12px]">{revokeTarget.key_prefix}…</span> will start getting{' '}
                  <code className="font-[var(--font-mono)] text-[12px] text-[var(--color-text-strong)]">401</code> responses immediately. This can&rsquo;t be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRevokeTarget(null)}
                disabled={revoking}
                className={[
                  'px-4 py-2 rounded-[var(--radius-DEFAULT)]',
                  'border border-[var(--color-border)] text-[13px]',
                  'text-[var(--color-text)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)]/50',
                  'transition-colors cursor-pointer disabled:opacity-60',
                ].join(' ')}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRevoke}
                disabled={revoking}
                className={[
                  'inline-flex items-center gap-1.5 px-4 py-2 rounded-[var(--radius-DEFAULT)]',
                  'bg-[var(--color-signal)] text-white text-[13px] font-medium',
                  'hover:bg-[var(--color-signal-hover)] transition-colors',
                  'cursor-pointer disabled:opacity-60',
                ].join(' ')}
              >
                <Trash2 size={13} aria-hidden />
                {revoking ? 'Revoking…' : 'Revoke key'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Existing keys */}
      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-[14px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
            Existing keys
          </h2>
          {keys.length > 0 ? (
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search
                  size={14}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-muted)]"
                  aria-hidden
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name…"
                  aria-label="Search keys by name"
                  className="w-44 rounded-[var(--radius-DEFAULT)] border border-[var(--color-border)] bg-[var(--color-surface)] py-1.5 pl-8 pr-3 text-[13px] text-[var(--color-text)] focus:outline-none focus:border-[var(--color-accent)]"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                aria-label="Filter by status"
                className="rounded-[var(--radius-DEFAULT)] border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 text-[13px] text-[var(--color-text)] cursor-pointer focus:outline-none focus:border-[var(--color-accent)]"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </select>
            </div>
          ) : null}
        </div>

        {loading ? (
          <p className="mt-4 text-[13px] text-[var(--color-muted)]">Loading…</p>
        ) : keys.length === 0 ? (
          <p className="mt-4 text-[13px] text-[var(--color-muted)]">
            No keys yet. Generate your first above.
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--color-border)]">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-[var(--color-elev)]">
                  <SortTh label="Name" col="name" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
                    Status
                  </th>
                  <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
                    Created by
                  </th>
                  <SortTh label="Last used" col="last_used_at" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                  <SortTh label="Expires" col="expires_at" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] font-[var(--font-mono)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleKeys.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-[13px] text-[var(--color-muted)]">
                      No keys match your filters.
                    </td>
                  </tr>
                ) : (
                  visibleKeys.map((k) => {
                    const s = statusOf(k)
                    return (
                      <tr key={k.id} className="border-t border-[var(--color-border-soft)]">
                        <td className="px-4 py-3">
                          <span className="text-[13.5px] font-medium text-[var(--color-text-strong)]">{k.name}</span>{' '}
                          <span className="font-[var(--font-mono)] text-[12px] text-[var(--color-muted)]">
                            {k.key_prefix}…
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={s} />
                        </td>
                        <td className="px-4 py-3 text-[13px] text-[var(--color-muted)] whitespace-nowrap">
                          {k.created_by ? (memberNames[k.created_by] ?? '—') : '—'}
                        </td>
                        <td className="px-4 py-3 text-[13px] text-[var(--color-muted)] whitespace-nowrap">
                          {shortDate(k.last_used_at)}
                        </td>
                        <td className="px-4 py-3 text-[13px] text-[var(--color-muted)] whitespace-nowrap">
                          {k.expires_at ? shortDate(k.expires_at) : 'never'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {s === 'active' ? (
                            <button
                              type="button"
                              onClick={() => setRevokeTarget(k)}
                              title="Revoke key"
                              className="p-1.5 rounded-[var(--radius-DEFAULT)] text-[var(--color-muted)] hover:text-[var(--color-signal)] hover:bg-[var(--color-signal-soft)] cursor-pointer"
                            >
                              <Trash2 size={14} aria-label="Revoke" />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AccountLayout>
  )
}

type SortCol = 'name' | 'last_used_at' | 'expires_at'

function SortTh({
  label,
  col,
  sortCol,
  sortDir,
  onSort,
}: {
  label: string
  col: SortCol
  sortCol: SortCol
  sortDir: 'asc' | 'desc'
  onSort: (col: SortCol) => void
}) {
  const active = sortCol === col
  const Icon = !active ? ChevronsUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown
  return (
    <th className="px-4 py-2.5">
      <button
        type="button"
        onClick={() => onSort(col)}
        aria-label={`Sort by ${label}`}
        className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] hover:text-[var(--color-text)] cursor-pointer font-[var(--font-mono)]"
      >
        {label}
        <Icon size={12} className={active ? 'text-[var(--color-accent)]' : 'opacity-50'} aria-hidden />
      </button>
    </th>
  )
}

function StatusBadge({ status }: { status: 'active' | 'expired' | 'revoked' }) {
  const cls = status === 'active'
    ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
    : status === 'expired'
      ? 'bg-[var(--color-signal-soft)] text-[var(--color-signal)]'
      : 'bg-[var(--color-border)] text-[var(--color-muted)]'
  return (
    <span className={`px-2 py-0.5 rounded-[var(--radius-DEFAULT)] text-[11px] font-medium capitalize ${cls}`}>
      {status}
    </span>
  )
}
